import { useParams } from 'react-router-dom'
import { useLazyQuery, useReactiveVar } from '@apollo/client'
import { setAlertQueue } from 'cache/alerts'
import { INTERNAL_DEAL_STATUS, setDealStatus } from 'cache/deal-status'
import { setShouldDisableActionBtns } from 'cache/form-valid'
import { BDME_CODE, DEAL_STATUS, latestDealVersionVar, setLatestDeal } from 'cache/latest-deal-info'
import SEARCH_DEAL, { IDeal, IDealSearchRequest, IDealSearchResponse } from 'queries/searchDeal'
import { useTokenExpiryHandler } from 'store/contexts/TokenExpWarn'
import { MessageType } from 'ts/enums/messageType.enums'
import { setIsFederalDealQueryVar } from '../cache/federal-deal-query'
import errorMessages from '../constants/errorMessages'
import { useEmployeeLookup } from './use-employee-lookup'
import { useLoadDealInfo } from './use-load-deal-info'
import { useUserProfile } from './use-user-profile'

export type dealLockFlagType = 'Y' | 'N'
const FEDERAL_DEAL = 'HPF'

const handleDealWarnings = (deal: IDeal) => {
  const { isDealExpired, freezeFlag, versionStatus, bdmeCode } = deal
  const isDealQuoted = versionStatus === DEAL_STATUS.QUOTED
  const isDealCancelled = versionStatus?.toLowerCase() === DEAL_STATUS.CANCELLED.toLowerCase()
  const isDealApprovedForDistribution = bdmeCode === BDME_CODE.APPROVED_DISTRIBUTION

  if (isDealExpired) {
    setAlertQueue({ type: MessageType.WARNING, message: errorMessages.DEAL_EXPIRED })
    setShouldDisableActionBtns(true)
  }

  if (isDealQuoted) {
    setAlertQueue({ type: MessageType.WARNING, message: errorMessages.DEAL_QUOTED })
    setShouldDisableActionBtns(true)
  }

  if (isDealCancelled) {
    setAlertQueue({ type: MessageType.WARNING, message: errorMessages.DEAL_CANCELLED })
    setShouldDisableActionBtns(true)
  }

  if (freezeFlag === 'Y') {
    setAlertQueue({ type: MessageType.WARNING, message: errorMessages.DEAL_FREEZED })
    setShouldDisableActionBtns(true)
  }

  if (isDealApprovedForDistribution && !isDealQuoted) {
    setAlertQueue({ type: MessageType.WARNING, message: errorMessages.DEAL_APPROVED_DISTRIBUTION })
  }

  if (versionStatus === DEAL_STATUS.DENIED) {
    setAlertQueue({ type: MessageType.WARNING, message: errorMessages.DEAL_DENIED })
  }
}

const handleDealLock = (
  dealLockFlag: string,
  dealLockUser: string,
  dealNumberValue: any,
  userId: string,
  checkLockByUser: (user: string, dealNum: any) => void
) => {
  if (dealLockFlag === 'Y') {
    setDealStatus(INTERNAL_DEAL_STATUS.LOCKED)
    if (userId && dealLockUser !== userId) {
      checkLockByUser(dealLockUser, dealNumberValue)
      setShouldDisableActionBtns(true)
    }
  }
}

export const useSearchDeal = () => {
  const { dealNumber } = useParams<{ dealNumber: string }>()
  const { dealVersionNumber } = useReactiveVar(latestDealVersionVar)
  const { checkLockByUser } = useEmployeeLookup()
  const { getDealInfo } = useLoadDealInfo()
  const { setDealNumber, setDealId, setUserId } = useTokenExpiryHandler()

  const [searchDeal, { data, loading, error }] = useLazyQuery<
    IDealSearchResponse,
    IDealSearchRequest
  >(SEARCH_DEAL, {
    fetchPolicy: 'cache-and-network',
  })
  const { userId } = useUserProfile()

  const processDealVersion = (findDealByVersion: IDeal) => {
    const { dealNumber: dealNum, bdId, dealLockFlag, dealLockUser } = findDealByVersion

    setDealNumber(dealNum)
    setDealId(bdId)
    setUserId(userId ?? '')
    setLatestDeal(findDealByVersion)

    if (findDealByVersion.businessModelCode) {
      getDealInfo(bdId)
    }

    handleDealWarnings(findDealByVersion)
    handleDealLock(dealLockFlag, dealLockUser, dealNum, userId ?? '', checkLockByUser)
  }

  const handleSearchDeal = async (tenant: string) => {
    searchDeal({
      variables: {
        dealSearchRequest: {
          dealFilter: 'AllVersions',
          searchBy: 'DealNumber',
          searchByEffectiveDate: false,
          searchByExpirationDate: false,
          searchByFirstQuoteDate: false,
          searchByRevisionDate: false,
          searchByOption: 'Contains',
          tenant,
          searchText: `${dealNumber}`,
        },
      },
    })
      .then((res) => {
        const deals = res?.data?.searchDeal?.deals
        if (deals) {
          const findDealByVersion = deals.find(
            (deal: IDeal) => dealVersionNumber === deal.dealVersionNumber
          )
          if (findDealByVersion) {
            processDealVersion(findDealByVersion)
          }
        }
        setIsFederalDealQueryVar(tenant === FEDERAL_DEAL)
      })
      .catch((error) => {
        return { error }
      })
  }

  if (data?.searchDeal?.deals) {
    const findDealByVersion = data.searchDeal.deals.find(
      (deal: IDeal) => dealVersionNumber === deal.dealVersionNumber
    )
    if (findDealByVersion) {
      setLatestDeal(findDealByVersion)
    }
  }

  return {
    searchDeal: handleSearchDeal,
    data,
    federalDeal: data?.searchDeal?.deals[0]?.tenantId === FEDERAL_DEAL,
    loading,
    error,
  }
}
